import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import './DebugPanel.css';

export function DebugPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [debugInfo, setDebugInfo] = useState({
    auth: {},
    firestore: { activeListeners: 0, pendingWrites: 0 },
    errors: [],
    rules: [],
    emulators: {
      firestore: process.env.REACT_APP_USE_EMULATORS === 'true',
      auth: process.env.REACT_APP_USE_EMULATORS === 'true',
      functions: process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === 'true',
      storage: process.env.REACT_APP_USE_EMULATORS === 'true',
    },
    build: {
      version: process.env.REACT_APP_VERSION || 'dev',
      environment: process.env.NODE_ENV,
      appCheckEnabled: process.env.REACT_APP_ENABLE_APP_CHECK === 'true',
    },
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const tokenResult = await user.getIdTokenResult();
          setDebugInfo((prev) => ({
            ...prev,
            auth: {
              uid: user.uid,
              email: user.email || undefined,
              emailVerified: user.emailVerified,
              claims: tokenResult.claims,
            },
          }));
        } catch (err) {
          console.error('DebugPanel token lookup failed', err);
          setDebugInfo((prev) => ({ ...prev, auth: { uid: user.uid } }));
        }
      } else {
        setDebugInfo((prev) => ({ ...prev, auth: {} }));
      }
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    const originalError = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      setDebugInfo((prev) => ({
        ...prev,
        errors: [
          {
            timestamp: new Date().toISOString(),
            message: String(message),
            stack: error && error.stack,
          },
          ...prev.errors.slice(0, 19),
        ],
      }));
      if (originalError) return originalError(message, source, lineno, colno, error);
      return false;
    };
    return () => { window.onerror = originalError; };
  }, []);

  useEffect(() => {
    const checkListeners = () => {
      try {
        const listeners = db._delegate && db._delegate._databaseId ? 1 : 0;
        setDebugInfo((prev) => ({
          ...prev,
          firestore: { ...prev.firestore, activeListeners: listeners },
        }));
      } catch (err) {
        console.error('DebugPanel listener check failed', err);
      }
    };
    const interval = setInterval(checkListeners, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleReseed = async () => {
    if (window.confirm('This will reset all emulator data. Continue?')) {
      try {
        const res = await fetch('/api/reseed', { method: 'POST' });
        if (res.ok) window.location.reload();
      } catch (e) {
        console.error('Failed to reseed:', e);
      }
    }
  };

  const handleClearCache = () => {
    if ('caches' in window) {
      caches.keys().then((names) => names.forEach((n) => caches.delete(n)));
    }
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  };

  const handleOpenEmulatorUI = () => {
    window.open('http://localhost:4000', '_blank');
  };

  if (process.env.NODE_ENV === 'production') return null;

  const urlParams = new URLSearchParams(window.location.search);
  if (!urlParams.get('debug')) return null;

  return (
    <>
      <button
        className="debug-toggle"
        onClick={() => setIsOpen(!isOpen)}
        title="Toggle Debug Panel"
      >
        🐛
      </button>

      {isOpen && (
        <div className="debug-panel">
          <div className="debug-header">
            <h3>Debug Panel</h3>
            <button onClick={() => setIsOpen(false)}>×</button>
          </div>

          <div className="debug-content">
            <section>
              <h4>Authentication</h4>
              <pre>{JSON.stringify(debugInfo.auth, null, 2)}</pre>
            </section>

            <section>
              <h4>Firestore</h4>
              <p>Active Listeners: {debugInfo.firestore.activeListeners}</p>
              <p>Pending Writes: {debugInfo.firestore.pendingWrites}</p>
            </section>

            <section>
              <h4>Emulators</h4>
              <div className="emulator-status">
                {Object.entries(debugInfo.emulators).map(([name, enabled]) => (
                  <span key={name} className={enabled ? 'enabled' : 'disabled'}>
                    {name}: {enabled ? '✅' : '❌'}
                  </span>
                ))}
              </div>
            </section>

            <section>
              <h4>Build</h4>
              <p>Version: {debugInfo.build.version}</p>
              <p>Environment: {debugInfo.build.environment}</p>
              <p>App Check: {debugInfo.build.appCheckEnabled ? 'Enabled' : 'Disabled'}</p>
            </section>

            <section>
              <h4>Recent Errors ({debugInfo.errors.length})</h4>
              <div className="error-list">
                {debugInfo.errors.map((err, i) => (
                  <div key={i} className="error-item">
                    <span className="timestamp">{new Date(err.timestamp).toLocaleTimeString()}</span>
                    <span className="message">{err.message}</span>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h4>Quick Actions</h4>
              <div className="debug-actions">
                <button onClick={handleReseed}>Reseed Data</button>
                <button onClick={handleClearCache}>Clear Cache</button>
                <button onClick={handleOpenEmulatorUI}>Open Emulator UI</button>
              </div>
            </section>
          </div>
        </div>
      )}
    </>
  );
}

if (typeof window !== 'undefined') {
  window.__DUCKERS_DEBUG__ = {
    reseed: async () => fetch('/api/reseed', { method: 'POST' }),
  };
} 